import type { IAuthClient } from '@micro-frontend-react/employee-experience/lib/IAuthClient';
import type { SagaIterator } from 'redux-saga';
import { call, getContext, put, takeLatest } from 'redux-saga/effects';

import {
  fetchEmailTemplatesApi,
  fetchTemplateVariablesApi,
  updateEmailTemplateApi,
  previewEmailTemplateApi,
  EmailTemplateApiError,
} from '../../Services/EmailTemplateService';
import { EmailTemplate, TemplateVariable, RenderedEmail } from '../../models/EmailTemplate';
import {
  fetchEmailTemplatesSuccess,
  fetchEmailTemplatesFailure,
  updateEmailTemplateSuccess,
  updateEmailTemplateFailure,
  previewEmailTemplateSuccess,
  previewEmailTemplateFailure,
  fetchTemplateVariablesSuccess,
  fetchTemplateVariablesFailure,
  updateEmailTemplate,
  previewEmailTemplate,
} from '../../Redux/EmailTemplates/actions';
import {
  FETCH_EMAIL_TEMPLATES,
  UPDATE_EMAIL_TEMPLATE,
  PREVIEW_EMAIL_TEMPLATE,
  FETCH_TEMPLATE_VARIABLES,
} from '../../Redux/EmailTemplates/action-types';

function auth(authClient: IAuthClient) {
  return Promise.resolve(authClient.acquireToken(__SCOPES__));
}

export function* fetchEmailTemplatesSaga(): SagaIterator {
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token: string = yield call(auth, authClient);
    const templates: EmailTemplate[] = yield call(fetchEmailTemplatesApi, token);
    yield put(fetchEmailTemplatesSuccess(templates));
  } catch (err) {
    const apiError = err as EmailTemplateApiError;
    yield put(fetchEmailTemplatesFailure(apiError.message || 'Failed to fetch templates', apiError.correlationId));
  }
}

export function* fetchTemplateVariablesSaga(): SagaIterator {
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token: string = yield call(auth, authClient);
    const variables: TemplateVariable[] = yield call(fetchTemplateVariablesApi, token);
    yield put(fetchTemplateVariablesSuccess(variables));
  } catch (err) {
    const apiError = err as EmailTemplateApiError;
    yield put(fetchTemplateVariablesFailure(apiError.message || 'Failed to fetch variables'));
  }
}

export function* updateEmailTemplateSaga(action: ReturnType<typeof updateEmailTemplate>): SagaIterator {
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token: string = yield call(auth, authClient);
    const { code, data } = action.payload;
    const updated: EmailTemplate = yield call(updateEmailTemplateApi, token, code, data);
    yield put(updateEmailTemplateSuccess(updated));
  } catch (err) {
    const apiError = err as EmailTemplateApiError;
    yield put(updateEmailTemplateFailure(apiError.message || 'Failed to update template', apiError.correlationId));
  }
}

export function* previewEmailTemplateSaga(action: ReturnType<typeof previewEmailTemplate>): SagaIterator {
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token: string = yield call(auth, authClient);
    const code = action.payload;
    const result: RenderedEmail = yield call(previewEmailTemplateApi, token, code);
    yield put(previewEmailTemplateSuccess(result));
  } catch (err) {
    const apiError = err as EmailTemplateApiError;
    yield put(previewEmailTemplateFailure(apiError.message || 'Failed to generate preview', apiError.correlationId));
  }
}

export function* emailTemplatesSaga(): SagaIterator {
  yield takeLatest(FETCH_EMAIL_TEMPLATES, fetchEmailTemplatesSaga);
  yield takeLatest(UPDATE_EMAIL_TEMPLATE, updateEmailTemplateSaga);
  yield takeLatest(PREVIEW_EMAIL_TEMPLATE, previewEmailTemplateSaga);
  yield takeLatest(FETCH_TEMPLATE_VARIABLES, fetchTemplateVariablesSaga);
}
